if (!customElements.get('product-back-in-stock')) {
  customElements.define('product-back-in-stock', class extends HTMLElement {
    connectedCallback() {
      const quickAddDrawer = this.closest('quick-add-drawer');
      if (quickAddDrawer) {
        quickAddDrawer.querySelector('[data-bis-sticky-proxy]')?.remove();
        this.remove();
        return;
      }

      this.productSection = this.closest('.js-product');
      this.purchaseSlot = this.closest('.product-info__add-button');
      this.purchaseRow = this.closest('[data-bis-purchase-row]');
      this.canonicalTrigger = this.querySelector('[data-bis-trigger]');
      this.nativeMainAction = this.productSection?.querySelector('[data-bis-native-main]');
      this.nativeStickyAction = this.productSection?.querySelector('[data-bis-native-sticky]');
      this.stickyProxy = this.productSection?.querySelector('[data-bis-sticky-proxy]');
      this.stickyActionSlot = this.stickyProxy?.parentElement;
      this.variantData = this.readVariantData();
      this.isActive = false;

      if (!this.productSection || !this.canonicalTrigger || this.dataset.productEligible !== 'true') {
        this.dataset.variantEligible = 'false';
        return;
      }

      this.productSection.addEventListener('on:variant:change', (event) => {
        const variant = event.detail && event.detail.variant;
        queueMicrotask(() => this.updateForVariant(variant));
      });

      this.canonicalTrigger.addEventListener('click', (event) => {
        if (!this.isActive) event.preventDefault();
      });

      this.stickyProxy?.addEventListener('click', () => {
        this.showKlaviyoActionInStickyBar();
      });

      this.triggerObserver = new MutationObserver(() => this.queueStateSync());
      this.triggerObserver.observe(this.purchaseSlot || this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style', 'aria-hidden'],
      });
      if (this.stickyActionSlot && this.stickyActionSlot !== this.purchaseSlot) {
        this.triggerObserver.observe(this.stickyActionSlot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'hidden', 'style', 'aria-hidden'],
        });
      }

      const initialVariant = this.variantData.find(
        (variant) => variant.id === Number(this.dataset.currentVariantId)
      );
      this.updateForVariant(initialVariant);
      window.addEventListener('load', () => this.queueStateSync(), { once: true });
    }

    disconnectedCallback() {
      this.triggerObserver?.disconnect();
    }

    readVariantData() {
      const node = this.querySelector('[data-bis-variants]');
      if (!node) return [];

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return [];
      }
    }

    isBackInStockVariantEligible(inventory) {
      return Boolean(
        inventory &&
        inventory.available === false &&
        inventory.inventoryManagement &&
        inventory.inventoryPolicy === 'deny' &&
        inventory.inventoryAtOrBelowZero
      );
    }

    updateForVariant(variant) {
      const inventory = variant
        ? this.variantData.find((item) => item.id === Number(variant.id))
        : null;
      const eligible = this.isBackInStockVariantEligible(inventory);

      this.dataset.variantEligible = String(eligible);
      if (!eligible && this.stickyActionLocked) {
        this.stickyActionLocked = false;
        this.restoreKlaviyoActionToMain();
      }
      this.queueStateSync();
    }

    queueStateSync() {
      if (this.stateSyncQueued) return;
      this.stateSyncQueued = true;

      requestAnimationFrame(() => {
        this.stateSyncQueued = false;
        this.syncActivatedState();
      });
    }

    isElementVisible(element) {
      if (!element || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

      const style = window.getComputedStyle(element);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        element.getClientRects().length > 0
      );
    }

    findKlaviyoContainer() {
      return (
        this.querySelector('#klaviyo-bis-button-container') ||
        this.stickyActionSlot?.querySelector('#klaviyo-bis-button-container') ||
        this.purchaseSlot?.querySelector('#klaviyo-bis-button-container')
      );
    }

    adoptKlaviyoContainer() {
      const container = this.findKlaviyoContainer();
      if (
        !container ||
        container.dataset.bisStickyAction === 'true' ||
        this.contains(container)
      ) {
        return container;
      }

      this.appendChild(container);
      return container;
    }

    findVisibleKlaviyoAction(container = this.findKlaviyoContainer()) {
      const dynamicButton = container?.querySelector('button');
      if (this.isElementVisible(dynamicButton)) return dynamicButton;
      if (this.isElementVisible(this.canonicalTrigger)) return this.canonicalTrigger;
      return null;
    }

    showKlaviyoActionInStickyBar() {
      const container = this.findKlaviyoContainer();
      const klaviyoAction = container?.querySelector('button');
      if (!this.isActive || !container || !this.isElementVisible(klaviyoAction)) return;

      if (!container.__bisHome) {
        container.__bisHome = {
          parent: container.parentNode,
          nextSibling: container.nextSibling,
        };
      }
      if (!this.stickyActionSlot) return;

      this.stickyProxy.hidden = true;
      container.dataset.bisStickyAction = 'true';
      this.stickyActionSlot.appendChild(container);
      klaviyoAction.focus();
      this.stickyActionLocked = true;
    }

    restoreKlaviyoActionToMain() {
      const container = this.stickyActionSlot?.querySelector(
        '#klaviyo-bis-button-container[data-bis-sticky-action="true"]'
      );
      const home = container?.__bisHome;
      if (!container || !home?.parent) {
        this.stickyActionLocked = false;
        return;
      }

      delete container.dataset.bisStickyAction;
      if (home.nextSibling?.parentNode === home.parent) {
        home.parent.insertBefore(container, home.nextSibling);
      } else {
        home.parent.appendChild(container);
      }
      this.stickyActionLocked = false;
    }

    syncActivatedState() {
      const eligible = this.dataset.variantEligible === 'true';
      const container = this.adoptKlaviyoContainer();
      const active = eligible && Boolean(this.findVisibleKlaviyoAction(container));
      this.setActive(active);
    }

    setActive(active) {
      if (!active && this.stickyActionLocked) this.restoreKlaviyoActionToMain();
      this.isActive = active;
      this.dataset.active = String(active);

      if (this.nativeMainAction) this.nativeMainAction.hidden = active;
      if (this.nativeStickyAction) this.nativeStickyAction.hidden = active;
      if (this.stickyProxy) {
        const dynamicActionInSticky = Boolean(
          this.stickyActionSlot?.querySelector(
            '#klaviyo-bis-button-container[data-bis-sticky-action="true"]'
          )
        );
        this.stickyProxy.hidden = !active || dynamicActionInSticky;
      }
      if (this.purchaseRow) this.purchaseRow.dataset.bisActive = String(active);
    }
  });
}
